import { get, isEqual, noop } from "lodash";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";

import { defaultDisplayValue } from "./defaultDisplayValue";
import { filterByLabel } from "./filters";
import { useSearchOptions } from "./useSearchOptions";

export interface UseSearchableSelectProps<Option = unknown, Value = unknown> {
  displayValue?: (option: Option) => string;
  filterBy?: (
    options: Option[] | readonly Option[],
    search: string,
  ) => Option[];
  groupByValue?: (option: Option) => string | undefined;
  initialValue?: Value | "";
  initialValuePath?: string;
  isError?: boolean;
  // Opt-in cap on the number of options rendered/searched through. Leave
  // unset to render every matching option (the default for all callers).
  maxVisibleOptions?: number;
  onChange?: (value: Value | "") => void;
  onChangeValue?: (option: Option | null) => Value | "";
  onSearchChange?: (search: string) => void;
  options?: Option[] | readonly Option[];
  optionsClassName?: string;
  placeholder?: string;
  value?: Value;
}

const findOptionByValue = <Option = unknown, Value = unknown>(
  options: Option[] | readonly Option[],
  value: Value | undefined | "",
  valuePath: string,
) => options.find(option => get(option, valuePath) === value);

const defaultOnChangeValue = <Option = unknown, Value = unknown>(
  option: Option,
): Value | "" => {
  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible
  if (typeof option === "object" && option !== null && "value" in option) {
    return option.value as Value;
  }
  return "";
};

export const useSearchableSelect = <Option = unknown, Value = unknown>({
  options = [],
  onChange = noop,
  isError = false,
  filterBy = filterByLabel,
  placeholder = t("select_an_option"),
  displayValue = defaultDisplayValue,
  onChangeValue = defaultOnChangeValue,
  onSearchChange = noop,
  groupByValue,
  value,
  initialValue,
  initialValuePath = "value",
  optionsClassName,
  maxVisibleOptions,
}: UseSearchableSelectProps<Option, Value>) => {
  const hasInitialValue = Boolean(initialValue) && Boolean(initialValuePath);
  const initialOption =
    (hasInitialValue &&
      findOptionByValue(options, initialValue, initialValuePath)) ||
    null;

  const [currentOption, setCurrentOption] = useState<Option | null>(
    initialOption,
  );
  const clearValue = (): void => setCurrentOption(null);

  const {
    search,
    clearSearch,
    handleSearchChange,
    searchResults,
    hasNoResults,
    hasMoreResults,
    groupedSearchResults,
    hasGroups,
  } = useSearchOptions(
    options,
    filterBy,
    groupByValue,
    onSearchChange,
    maxVisibleOptions,
  );

  const selectProps = {
    value: currentOption,
    onFilter: handleSearchChange,
    onClose: clearSearch,
    onChange: (option: Option | null): void => {
      if (isEqual(value, get(option, initialValuePath))) return;
      const extractedValue = onChangeValue(option);
      setCurrentOption(option);
      onChange(extractedValue);
    },
    placeholder,
    displayValue,
    isError,
    optionsClassName,
  };

  const dropdownProps = {
    ...selectProps,
    nullable: true as const,
    onClose: (): void => {
      clearValue();
      clearSearch();
    },
  };

  useEffect(() => {
    if (!initialValue) {
      clearValue();
    }
  }, [initialValue]);

  useEffect(() => {
    if (get(currentOption, initialValuePath) === value) return;

    const foundOption = findOptionByValue(options, value, initialValuePath);
    foundOption ? setCurrentOption(foundOption) : clearValue();
  }, [currentOption, initialValuePath, options, value]);

  return {
    search,
    selectProps,
    dropdownProps,
    searchResults,
    hasNoResults,
    hasMoreResults,
    groupedSearchResults,
    hasGroups,
  };
};
